import React, { useMemo } from 'react';
import { Tag, Typography, Button, Space } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import { createFilterDefinitions } from './filters/config/FilterDefinitions';
import type { FilterDefinition } from './filters/types';
import { useLocationLabel } from '../../utils/locationLabel';

interface DimensionRange {
	min: number;
	max: number;
}

interface DimensionRanges {
	height?: DimensionRange;
	width?: DimensionRange;
	outer_diameter?: DimensionRange;
	inner_diameter?: DimensionRange;
	wall?: DimensionRange;
	current_length?: DimensionRange;
}

export interface AppliedFiltersBarProps {
	queryParams: URLSearchParams;
	navigate: (opts: { search: string }) => void;
	shapes?: Array<{ id: string; display_name: string }>;
	categories?: Array<{ id: string; display_name: string }>;
	types?: Array<{ id: string; display_name: string }>;
	buildings?: Array<{ id: string; name: string }>;
	locations?: Array<{ id: string; display_name?: string; code?: string }>;
	dimensionRanges?: DimensionRanges;
	/**
	 * Extra simple URL keys (single-string values) that should also be displayed as chips.
	 * The page-level Search input uses `search` and is included by default.
	 * Pass keys with a friendly label, e.g. `[{ key: 'search', label: 'Search' }]`.
	 */
	extraKeys?: Array<{ key: string; label: string }>;
	/** Optional className/style passthrough so each page can adjust spacing. */
	className?: string;
	style?: React.CSSProperties;
}

interface Chip {
	id: string;
	label: string;
	valueLabel: string;
	onClose: () => void;
}

const DEFAULT_EXTRA_KEYS: Array<{ key: string; label: string }> = [
	{ key: 'search', label: 'Search' },
];

const RANGE_KEY_TO_API_RANGE: Record<string, keyof DimensionRanges> = {
	heightRange: 'height',
	widthRange: 'width',
	outerDiameterRange: 'outer_diameter',
	innerDiameterRange: 'inner_diameter',
	wallThicknessRange: 'wall',
	currentLengthRange: 'current_length',
};

const AppliedFiltersBar: React.FC<AppliedFiltersBarProps> = ({
	queryParams,
	navigate,
	shapes = [],
	categories = [],
	types = [],
	buildings = [],
	locations = [],
	dimensionRanges,
	extraKeys = DEFAULT_EXTRA_KEYS,
	className,
	style,
}) => {
	const formatLocation = useLocationLabel();
	const shapeIdMap = useMemo(
		() =>
			shapes.reduce((acc: Record<string, string>, s) => {
				acc[s.display_name] = s.id;
				return acc;
			}, {}),
		[shapes]
	);

	const definitions: FilterDefinition[] = useMemo(
		() => createFilterDefinitions(shapeIdMap),
		[shapeIdMap]
	);

	const labelByOption = useMemo(() => {
		const map: Record<string, Map<string, string>> = {
			shapes: new Map(shapes.map((s) => [s.id, s.display_name])),
			categories: new Map(categories.map((c) => [c.id, c.display_name])),
			types: new Map(types.map((t) => [t.id, t.display_name])),
			buildings: new Map(buildings.map((b) => [b.id, b.name])),
			locations: new Map(
				locations.map((l) => [
					l.id,
					formatLocation(l.display_name) || l.code || l.id,
				])
			),
		};
		return map;
	}, [shapes, categories, types, buildings, locations, formatLocation]);

	const setOrDeleteKey = (mutations: Array<{ key: string; value?: string | null }>) => {
		const params = new URLSearchParams(queryParams.toString());
		for (const m of mutations) {
			if (m.value == null || m.value === '') {
				params.delete(m.key);
			} else {
				params.set(m.key, m.value);
			}
		}
		params.set('page', '1');
		navigate({ search: params.toString() });
	};

	const resolveStaticLabel = (def: FilterDefinition, value: string): string => {
		const propKey =
			(def as any).optionsFromProp ??
			(def as any).optionGroupsFromProp ??
			null;
		if (propKey && labelByOption[propKey as keyof typeof labelByOption]) {
			const label = labelByOption[propKey as keyof typeof labelByOption].get(value);
			if (label) return label;
		}
		const staticOpts = (def as any).options as
			| Array<{ value: string; label: string }>
			| undefined;
		if (staticOpts) {
			const found = staticOpts.find((o) => o.value === value);
			if (found) return found.label;
		}
		return value;
	};

	const chips = useMemo<Chip[]>(() => {
		const out: Chip[] = [];

		for (const extra of extraKeys) {
			const value = queryParams.get(extra.key);
			if (!value) continue;
			out.push({
				id: extra.key,
				label: extra.label,
				valueLabel: value,
				onClose: () => setOrDeleteKey([{ key: extra.key }]),
			});
		}

		for (const def of definitions) {
			switch (def.type) {
				case 'range': {
					const minKey = def.urlKeys.min;
					const maxKey = def.urlKeys.max;
					const urlMin = queryParams.get(minKey);
					const urlMax = queryParams.get(maxKey);
					if (!urlMin && !urlMax) break;

					const apiRangeKey = def.initialRangeKey
						? RANGE_KEY_TO_API_RANGE[def.initialRangeKey]
						: undefined;
					const fullRange = apiRangeKey
						? dimensionRanges?.[apiRangeKey]
						: undefined;

					const minVal = urlMin != null ? Number(urlMin) : fullRange?.min;
					const maxVal = urlMax != null ? Number(urlMax) : fullRange?.max;

					if (
						fullRange &&
						minVal === fullRange.min &&
						maxVal === fullRange.max
					) {
						break;
					}

					const fmt = (n: number | undefined) =>
						n != null && Number.isFinite(n) ? n.toFixed(2) : '?';
					const suffix = def.suffix ? ` ${def.suffix}` : '';
					out.push({
						id: def.key,
						label: def.label,
						valueLabel: `${fmt(minVal)} – ${fmt(maxVal)}${suffix}`,
						onClose: () =>
							setOrDeleteKey([{ key: minKey }, { key: maxKey }]),
					});
					break;
				}
				case 'radio':
				case 'search-input': {
					const v = queryParams.get(def.urlKeys.value);
					if (!v) break;
					out.push({
						id: def.key,
						label: def.label,
						valueLabel: resolveStaticLabel(def, v),
						onClose: () =>
							setOrDeleteKey([{ key: def.urlKeys.value }]),
					});
					break;
				}
				case 'checkbox':
				case 'select': {
					const raw = queryParams.get(def.urlKeys.value);
					if (!raw) break;
					const arr = raw.split(',').filter(Boolean);
					arr.forEach((item) => {
						out.push({
							id: `${def.key}:${item}`,
							label: def.label,
							valueLabel: resolveStaticLabel(def, item),
							onClose: () => {
								const remaining = arr.filter((x) => x !== item);
								if (remaining.length === 0) {
									setOrDeleteKey([{ key: def.urlKeys.value }]);
								} else {
									setOrDeleteKey([
										{
											key: def.urlKeys.value,
											value: remaining.join(','),
										},
									]);
								}
							},
						});
					});
					break;
				}
			}
		}

		return out;
	}, [definitions, queryParams, dimensionRanges, extraKeys, labelByOption]);

	if (chips.length === 0) return null;

	const handleClearAll = () => {
		const params = new URLSearchParams(queryParams.toString());
		for (const extra of extraKeys) params.delete(extra.key);
		for (const def of definitions) {
			Object.values(def.urlKeys).forEach((k) => params.delete(k));
		}
		params.set('page', '1');
		navigate({ search: params.toString() });
	};

	return (
		<div
			className={className}
			style={{
				display: 'flex',
				flexWrap: 'wrap',
				alignItems: 'center',
				gap: 6,
				padding: '6px 8px',
				background: '#fafafa',
				border: '1px solid #f0f0f0',
				borderRadius: 4,
				...style,
			}}
		>
			<Typography.Text type="secondary" style={{ fontSize: 12 }}>
				Filters:
			</Typography.Text>
			<Space size={4} wrap>
				{chips.map((chip) => (
					<Tag
						key={chip.id}
						closable
						onClose={(e) => {
							e.preventDefault();
							chip.onClose();
						}}
						style={{ marginInlineEnd: 0 }}
					>
						<span style={{ fontWeight: 500 }}>{chip.label}:</span>{' '}
						{chip.valueLabel}
					</Tag>
				))}
			</Space>
			<Button
				type="link"
				size="small"
				icon={<ClearOutlined />}
				onClick={handleClearAll}
				style={{ marginLeft: 'auto', padding: 0, height: 'auto' }}
			>
				Clear all
			</Button>
		</div>
	);
};

export default AppliedFiltersBar;
